import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { AuthGate } from "@/auth/AuthGate";
import { AppShell } from "@/components/chrome/AppShell";
import { Splash } from "@/components/chrome/Splash";
import { RouteError } from "@/components/chrome/RouteError";
// In the main bundle, not lazy: a signed-out visitor sees sign-in without another download first.
import Login from "@/screens/Login";

const AuthCallback = lazy(() => import("@/screens/AuthCallback"));
const Home = lazy(() => import("@/screens/Home"));
const PlanTab = lazy(() => import("@/screens/PlanTab"));
const Illustration = lazy(() => import("@/screens/Illustration"));
const PolicyDetail = lazy(() => import("@/screens/PolicyDetail"));
const Calculator = lazy(() => import("@/screens/Calculator"));
const Application = lazy(() => import("@/screens/Application"));
const ApplicationSummary = lazy(() => import("@/screens/ApplicationSummary"));
const Learn = lazy(() => import("@/screens/Learn"));
const Faq = lazy(() => import("@/screens/Faq"));
const CalculatorScreen = lazy(() => import("@/screens/CalculatorScreen"));
const Compare = lazy(() => import("@/screens/Compare"));
const Glossary = lazy(() => import("@/screens/Glossary"));
const Chat = lazy(() => import("@/screens/Chat"));
const Appointments = lazy(() => import("@/screens/Appointments"));
const Team = lazy(() => import("@/screens/Team"));
const Journey = lazy(() => import("@/screens/Journey"));
const Profile = lazy(() => import("@/screens/Profile"));
const Numbers = lazy(() => import("@/screens/Numbers"));
const SignIn = lazy(() => import("@/screens/SignIn"));

/** Signed-in screens: by the time one loads, the app knows whose plan it is. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Splash label="Loading your plan" />}>{children}</Suspense>;
}

function RedirectPolicy() {
  const { id } = useParams();
  return <Navigate to={`/plan/policy/${id ?? ""}`} replace />;
}

function RedirectStep() {
  const { stepId } = useParams();
  return <Navigate to={`/plan/application/${stepId ?? ""}`} replace />;
}

/**
 * Five tabs (Home, Plan, Learn, Messages, Profile), the same for prospects and
 * clients. Detail screens live under their tab's path so the tab stays lit,
 * like a SwiftUI NavigationStack inside each Tab. Old paths redirect.
 */
export const router = createBrowserRouter([
  { path: "/login", element: <Login />, errorElement: <RouteError /> },
  { path: "/auth/callback", element: <Suspense fallback={<Splash />}><AuthCallback /></Suspense>, errorElement: <RouteError /> },
  // 6c.5: the welcome email's one-tap sign-in link (and the iPhone app's universal link, /.well-known/apple-app-site-association)
  { path: "/signin", element: <Suspense fallback={<Splash label="Signing you in" />}><SignIn /></Suspense>, errorElement: <RouteError /> },
  {
    element: <AuthGate />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Lazy><Home /></Lazy> },
          { path: "appointments", element: <Lazy><Appointments /></Lazy> },
          { path: "team", element: <Lazy><Team /></Lazy> },
          { path: "journey", element: <Lazy><Journey /></Lazy> },

          { path: "plan", element: <Lazy><PlanTab /></Lazy> },
          { path: "plan/illustration/:id", element: <Lazy><Illustration /></Lazy> },
          { path: "plan/policy/:id", element: <Lazy><PolicyDetail /></Lazy> },
          { path: "plan/calculator", element: <Lazy><Calculator /></Lazy> },
          { path: "plan/numbers", element: <Lazy><Numbers /></Lazy> },
          { path: "plan/application", element: <Lazy><ApplicationSummary /></Lazy> },
          { path: "plan/application/status", element: <Navigate to="/journey" replace /> },
          { path: "plan/application/:stepId", element: <Lazy><Application /></Lazy> },

          { path: "learn", element: <Lazy><Learn /></Lazy> },
          { path: "learn/faq", element: <Lazy><Faq /></Lazy> },
          { path: "learn/calculators/:slug", element: <Lazy><CalculatorScreen /></Lazy> },
          { path: "learn/compare", element: <Lazy><Compare /></Lazy> },
          { path: "learn/glossary", element: <Lazy><Glossary /></Lazy> },

          { path: "messages", element: <Lazy><Chat /></Lazy> },

          { path: "profile", element: <Lazy><Profile /></Lazy> },

          // Paths from the first version of the app.
          { path: "chat", element: <Navigate to="/messages" replace /> },
          { path: "more", element: <Navigate to="/profile" replace /> },
          { path: "strategist", element: <Navigate to="/team" replace /> },
          { path: "faq", element: <Navigate to="/learn/faq" replace /> },
          { path: "calculator", element: <Navigate to="/plan/calculator" replace /> },
          { path: "products", element: <Navigate to="/plan" replace /> },
          { path: "products/:id", element: <RedirectPolicy /> },
          { path: "application", element: <Navigate to="/plan/application" replace /> },
          { path: "application/status", element: <Navigate to="/journey" replace /> },
          { path: "application/:stepId", element: <RedirectStep /> },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
